import * as React from "react";
import { useLocation } from "wouter";
import { MapPin, CalendarClock, Zap, ArrowRight, ArrowLeft, Plus } from "lucide-react";

/**
 * Start something — the Organize launcher surface (Organize-hub Option C).
 *
 * Option C takes the "mixed motives" diagnosis literally and splits the hub's two
 * jobs onto two surfaces: the hub (`/organize`) is now a pure *status* view, and this
 * page is the *launcher*. It's a small, decisive menu — the three top-level
 * organizing categories (grouped by nature of the work), each fanning out to its
 * existing clean landing → leaf tools. No IA change below the launcher; this is the
 * quality bar the category landings already hit, applied one level up.
 *
 * Reached from a persistent "Start something" action in the hub header (and, on
 * desktop, directly from the left sub-nav, which already lists the three categories).
 */
type CategoryCard = {
  key: string;
  label: string;
  hint: string;
  icon: React.ElementType;
  href: string;
};

const CATEGORIES: CategoryCard[] = [
  { key: "placement", label: "Placement", hint: "A place for everything — decide where things should live", icon: MapPin, href: "/organize/placement" },
  { key: "events", label: "Events", hint: "One-off organizing for a trip or occasion", icon: CalendarClock, href: "/organize/events" },
  { key: "upkeep", label: "Keep it up", hint: "Recurring tidy-ups and quick resets", icon: Zap, href: "/organize/upkeep" },
];

export default function StartPage() {
  const [, navigate] = useLocation();

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <header>
        <button
          onClick={() => navigate("/organize")}
          className="inline-flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors mb-2"
        >
          <ArrowLeft className="w-4 h-4" />Organize
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Plus className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium text-primary">Start something</span>
        </div>
        <h1 className="text-4xl font-semibold text-ink font-headline tracking-[-0.02em]">What are you organizing?</h1>
        <p className="text-body mt-1 text-sm">Pick where to begin — each opens to the right tools for the job.</p>
      </header>

      <div className="space-y-3">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <a
              key={cat.key}
              href={cat.href}
              className="flex items-center gap-3 bg-white rounded-lg border border-clay-border p-5 hover:border-primary/40 transition-colors group"
            >
              <div className="w-11 h-11 rounded-md flex items-center justify-center shrink-0 bg-amethyst-soft">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[1.0625rem] font-semibold text-ink">{cat.label}</div>
                <div className="text-[0.8125rem] text-faint">{cat.hint}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
